// @services/stubs/StubLifecycleClient.ts
import type { LifecycleClient } from '@services/clients/LifecycleClient';
import type { DocumentID } from "@ps-bridge/models/core/dataContracts";
import type {
    CreateDocumentEditRequest,
    CreateDocumentExampleRequest,
    CreateDocumentResponse,
    CreateDocumentScratchRequest,
    DocumentFetchResponse,
    DocumentMetadata,
    LifecycleError,
    ListAvailableDocumentsResponse,
    OutlineResponse,
    UploadSourceDocumentRequest,
    UploadSourceDocumentResponse
} from "@ps-bridge/models/core/dataContracts";
import type { OptionTs, TaskEitherTs } from '@ps-bridge/interop';
import * as E from 'fp-ts/lib/Either';

import { ok } from "@ps-bridge/ok";
import type { Ok } from "@ps-bridge/ok";
import { makeDocumentID } from "@ps-bridge/models/brands/documentID";
import { makeSectionID } from "@ps-bridge/models/brands/sectionID";
import { makeSectionIndex } from "@ps-bridge/models/brands/sectionIndex";
import { makeSectionStatus } from "@ps-bridge/models/brands/sectionStatus";
import { makeIsoTimestamp } from "@ps-bridge/models/brands/isoTimestamp";
import { makeErrorMessageText } from "@ps-bridge/models/brands/errorMessageText";
import * as PSOption from "@utils/Option";
import { taskEitherFromRaw } from '@utils/effects/branded';

type CreateDocumentRequest =
    | CreateDocumentScratchRequest
    | CreateDocumentEditRequest
    | CreateDocumentExampleRequest;

type JsonValue = string | number | boolean | JsonObject | readonly JsonValue[];
type JsonObject = { readonly [key: string]: JsonValue };

const delay = (ms: number): Promise<Ok> =>
    new Promise((resolve) => setTimeout(() => resolve(ok), ms));

const delayedRight = <Error, Value>(
    ms: number,
    value: Value,
    onRun?: () => Ok,
): TaskEitherTs<Error, Value> =>
    taskEitherFromRaw(async () => {
        onRun?.();
        await delay(ms);
        return E.right(value);
    });

const delayedLeft = <Error, Value>(
    ms: number,
    error: Error,
    onRun?: () => Ok,
): TaskEitherTs<Error, Value> =>
    taskEitherFromRaw(async () => {
        onRun?.();
        await delay(ms);
        return E.left(error);
    });

const SAMMIE_STORY_PROMPT = `
## Understanding the Vision

You’ve expressed a desire to create a light-hearted slice of life story centered around a cat named Sammie. The story is outlined in three sections: **“A Beautiful Morning,”** **“Dark Clouds Arrive,”** and **“A Cozy Nook.”** Each section seems to imply a progression in Sammie’s day, potentially starting from an idyllic morning, transitioning to a period of challenge or uncertainty, and concluding in comfort or resolution.

## Clarifying Questions

1. **Can you share more about Sammie’s character traits and how they influence the narrative?**
2. **What tone or mood do you want to establish in each section, particularly when transitioning from “A Beautiful Morning” to “Dark Clouds Arrive”?**
3. **Are there any specific events or themes you envision each section exploring?**
4. **What is the primary message or takeaway you want readers to have from Sammie’s story?**
5. **Are there any other characters or elements that play a significant role in the story?**
`;

const SAMMIE_STORY_INTRO = `
Sammie stretched luxuriously, her claws extending outward as she yawned, the warmth of the sun enveloping her like a soft, invisible blanket. The garden was her kingdom, a place where every nook and cranny had been explored and deemed worthy—or not—of her regal presence. Today, the sun-warmed stone at the garden's edge was the chosen throne, its smooth surface soaking up the morning's golden rays.

The air was filled with the scent of blooming flowers, a sweet symphony that played softly in the background of Sammie's world. Birds chirped their morning songs, their melodies dancing on the gentle breeze that occasionally ruffled Sammie's fur. She blinked lazily, her emerald eyes half-closed in contentment.

Through her keen senses, Sammie perceived the subtle shifts in the environment. The stone beneath her was comfortably warm, but the shadows it cast were slowly inching closer, a silent reminder of the day’s progress. Her whiskers twitched as she considered her options, a mental map of the garden unfurling in her mind. The perfect sunny spot was a coveted prize, one that required vigilance and decisiveness to claim.

As she lay there, Sammie pondered the transient nature of her sunlit haven. The clouds in the distance were merely a whisper on the horizon, but they carried with them the promise of change. For now, though, she was content to bask in the moment, her world distilled into simple pleasures and the pursuit of comfort.

The garden was hers, and in this peaceful morning, Sammie was the undisputed ruler of her domain, aware yet unfazed by the inevitable shifts that awaited. For Sammie, the quest for the perfect lounging spot was both a daily ritual and a testament to her discerning nature.
`;

const STUB_LIFECYCLE_MODE_KEY = "stub:lifecycle:mode";
const STUB_LIFECYCLE_ERROR_MESSAGE_KEY = "stub:lifecycle:errorMessage";
const STUB_LIFECYCLE_ERROR_TYPE_KEY = "stub:lifecycle:errorType";
const STUB_LIFECYCLE_AVAILABLE_DOCUMENTS_KEY = "stub:lifecycle:availableDocuments";
const STUB_LIFECYCLE_LAST_CREATE_REQUEST_KEY = "stub:lifecycle:lastCreateDocumentRequest";
const STUB_LIFECYCLE_LAST_SOURCE_UPLOAD_REQUEST_KEY = "stub:lifecycle:lastSourceUploadRequest";
const DEFAULT_LIFECYCLE_ERROR_MESSAGE = "Unable to load documents.";
const DEFAULT_LIFECYCLE_ERROR_TYPE = "NetworkError";

const getStubStorageValue = (key: string): OptionTs<string> => {
    if (typeof window === "undefined") {
        return PSOption.none();
    }
    try {
        const value = window.localStorage.getItem(key);
        return typeof value === "string" ? PSOption.some(value) : PSOption.none();
    } catch (error) {
        console.warn("StubLifecycleClient failed to read localStorage", error);
        return PSOption.none();
    }
};

const getStubLifecycleMode = (): "success" | "error" =>
    PSOption.fold(
        getStubStorageValue(STUB_LIFECYCLE_MODE_KEY),
        () => "success",
        (value) => (value === "error" ? "error" : "success"),
    );

const buildLifecycleError = (): LifecycleError => ({
    type: PSOption.fold(
        getStubStorageValue(STUB_LIFECYCLE_ERROR_TYPE_KEY),
        () => DEFAULT_LIFECYCLE_ERROR_TYPE,
        (value) => value,
    ),
    message: makeErrorMessageText(
        PSOption.fold(
            getStubStorageValue(STUB_LIFECYCLE_ERROR_MESSAGE_KEY),
            () => DEFAULT_LIFECYCLE_ERROR_MESSAGE,
            (value) => value,
        ),
    ),
    statusCode: PSOption.none(),
});

const isJsonObject = (value: JsonValue): value is JsonObject =>
    Boolean(value) && typeof value === "object" && !Array.isArray(value);

const parseStubDocumentMetadata = (value: JsonValue): OptionTs<DocumentMetadata> => {
    if (!isJsonObject(value)) {
        return PSOption.none();
    }
    const id = value.id;
    const name = value.name;
    const lastEdited = value.last_edited;
    if (typeof id !== "string" || typeof name !== "string" || typeof lastEdited !== "string") {
        return PSOption.none();
    }
    return PSOption.some({
        id: makeDocumentID(id),
        name,
        last_edited: makeIsoTimestamp(lastEdited),
    });
};

// Helper function to generate documents with dates
const generateStubDocuments = (count: number, daysApart: number): DocumentMetadata[] => {
    return Array.from({ length: count }, (_, index) => {
        const date = new Date();
        date.setDate(date.getDate() - index * daysApart);
        return {
            id: makeDocumentID(`stub-document-id-${index + 1}`),
            name: `Stub Document ${index + 1}`,
            last_edited: makeIsoTimestamp(date.toISOString()),
        };
    });
};

const readFixtureDocuments = (): DocumentMetadata[] =>
    PSOption.fold(
        getStubStorageValue(STUB_LIFECYCLE_AVAILABLE_DOCUMENTS_KEY),
        () => generateStubDocuments(12, 5),
        (value) => {
            try {
                const parsed = JSON.parse(value) as JsonValue;
                if (!Array.isArray(parsed)) {
                    return generateStubDocuments(12, 5);
                }
                const documents = parsed.flatMap((item): DocumentMetadata[] =>
                    PSOption.fold(
                        parseStubDocumentMetadata(item),
                        () => [],
                        (document) => [document],
                    ),
                );
                return documents.length > 0 ? documents : generateStubDocuments(12, 5);
            } catch (error) {
                console.warn("StubLifecycleClient failed to parse available document fixture", error);
                return generateStubDocuments(12, 5);
            }
        },
    );

const writeFixtureDocuments = (documents: DocumentMetadata[]): Ok => {
    if (typeof window === "undefined") {
        return ok;
    }
    try {
        window.localStorage.setItem(STUB_LIFECYCLE_AVAILABLE_DOCUMENTS_KEY, JSON.stringify(documents));
    } catch (error) {
        console.warn("StubLifecycleClient failed to write available document fixture", error);
    }
    return ok;
};

const writeLastCreateDocumentRequest = (request: CreateDocumentRequest): Ok => {
    if (typeof window === "undefined") {
        return ok;
    }
    try {
        window.localStorage.setItem(STUB_LIFECYCLE_LAST_CREATE_REQUEST_KEY, JSON.stringify(request));
    } catch (error) {
        console.warn("StubLifecycleClient failed to write last create request", error);
    }
    return ok;
};

const writeLastSourceUploadRequest = (request: UploadSourceDocumentRequest): Ok => {
    if (typeof window === "undefined") {
        return ok;
    }
    try {
        window.localStorage.setItem(STUB_LIFECYCLE_LAST_SOURCE_UPLOAD_REQUEST_KEY, JSON.stringify(request));
    } catch (error) {
        console.warn("StubLifecycleClient failed to write last source upload request", error);
    }
    return ok;
};

const uploadedSourceIdFromFileName = (fileName: string): DocumentID => {
    const normalized = fileName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    return makeDocumentID(`stub-uploaded-source-${normalized || "document"}`);
};

export class StubLifecycleClient implements LifecycleClient {
    createDocument(
        request: CreateDocumentRequest,
    ): TaskEitherTs<LifecycleError, CreateDocumentResponse> {
        console.log('StubLifecycleClient.createDocument called', request);
        writeLastCreateDocumentRequest(request);
        if (getStubLifecycleMode() === "error") {
            return delayedLeft(100, buildLifecycleError());
        }
        return delayedRight(100, {
            prompting_question: SAMMIE_STORY_PROMPT,
            document_id: makeDocumentID('stub-document-id'),
        });
    }

    uploadSourceDocument(
        request: UploadSourceDocumentRequest,
    ): TaskEitherTs<LifecycleError, UploadSourceDocumentResponse> {
        console.log('StubLifecycleClient.uploadSourceDocument called', request);
        writeLastSourceUploadRequest(request);
        if (getStubLifecycleMode() === "error") {
            return delayedLeft(100, buildLifecycleError());
        }
        const documents = readFixtureDocuments();
        const uploadedDocument: DocumentMetadata = {
            id: uploadedSourceIdFromFileName(request.file_name),
            name: request.file_name,
            last_edited: makeIsoTimestamp(new Date().toISOString()),
        };
        writeFixtureDocuments([
            uploadedDocument,
            ...documents.filter((document) => document.id !== uploadedDocument.id),
        ]);
        return delayedRight(100, { document: uploadedDocument });
    }

    fetchOutline(documentId: DocumentID): TaskEitherTs<LifecycleError, OutlineResponse> {
        console.log('StubLifecycleClient.fetchOutline called', documentId);
        return delayedRight(100, {
            outline: [
                {
                    id: makeSectionID('stub-section-id-1'),
                    name: 'Introduction',
                    index: makeSectionIndex(1),
                    status: makeSectionStatus('Active'),
                    last_edited: PSOption.some(makeIsoTimestamp(new Date().toISOString())),
                },
                {
                    id: makeSectionID('stub-section-id-2'),
                    name: 'Conclusion',
                    index: makeSectionIndex(2),
                    status: makeSectionStatus('Unstarted'),
                    last_edited: PSOption.none(),
                },
            ],
        });
    }

    fetchDocument(documentId: DocumentID): TaskEitherTs<LifecycleError, DocumentFetchResponse> {
        console.log('StubLifecycleClient.fetchDocument called', documentId);
        return delayedRight(100, {
            id: documentId,
            name: 'Stub Document',
            document_contents: [
                {
                    section_contents: PSOption.some(SAMMIE_STORY_INTRO),
                    metadata: {
                        id: makeSectionID('stub-section-id-1'),
                        name: 'Introduction',
                        index: makeSectionIndex(1),
                        status: makeSectionStatus('Active'),
                        last_edited: PSOption.some(makeIsoTimestamp(new Date().toISOString())),
                    },
                },
                {
                    section_contents: PSOption.none(),
                    metadata: {
                        id: makeSectionID('stub-section-id-2'),
                        name: 'Conclusion',
                        index: makeSectionIndex(2),
                        status: makeSectionStatus('Active'),
                        last_edited: PSOption.some(makeIsoTimestamp(new Date().toISOString())),
                    },
                },
            ],
        });
    }

    listAvailableDocuments: TaskEitherTs<LifecycleError, ListAvailableDocumentsResponse> =
        taskEitherFromRaw(async () => {
            if (getStubLifecycleMode() === "error") {
                return E.left(buildLifecycleError());
            }
            await delay(100);
            console.log('StubLifecycleClient.listAvailableDocuments called');
            return E.right({
                documents: readFixtureDocuments(),
            });
        });

    downloadDocumentAsBlob(documentId: DocumentID): TaskEitherTs<LifecycleError, Blob> {
        console.log('StubLifecycleClient.downloadDocumentAsBlob called', documentId);

        const blobContent = 'This is a stub document for download.';
        const blob = new Blob([blobContent], { type: 'application/octet-stream' });

        return delayedRight(100, blob);
    }

    deleteDocument(documentId: DocumentID): TaskEitherTs<LifecycleError, Ok> {
        console.log('StubLifecycleClient.deleteDocument called', documentId);
        return delayedRight(100, ok);  // Simulate successful deletion
    }
}
